import type { GenerationFailure } from './generation-state'
import type { ShaderGenerationProgress } from '@/lib/canvas/shader/authoring/types'

type State = Record<string, unknown>
export type StateChange = (updater: (prev: State) => State) => void

/**
 * In-flight shader generations, keyed by editor scope and node. They live
 * outside React so a node that unmounts while one runs (React Flow drops
 * off-screen nodes) does not cancel it: the result still lands, and a remounted
 * node picks the run back up. The scope keeps two editors apart even when
 * their nodes share ids (a duplicated canvas keeps its node ids).
 *
 * A run ends when it settles, when the user cancels it, when its node is
 * removed, or when its editor goes away (see ShaderGenerationScope).
 */
export interface ActiveRun {
  scope: string
  nodeId: string
  controller: AbortController
  /** The newest state updater a mounted instance of the node registered. */
  apply: StateChange | undefined
}

export interface RunSnapshot {
  running: boolean
  progress: ShaderGenerationProgress | null
  error: GenerationFailure | null
  completed: number
}

export const IDLE_RUN: RunSnapshot = { running: false, progress: null, error: null, completed: 0 }

/** The scope used outside any ShaderGenerationScope (tests, stand-alone renders). */
export const DEFAULT_GENERATION_SCOPE = 'default'

const activeRuns = new Map<string, ActiveRun>()
const snapshots = new Map<string, RunSnapshot>()
const listeners = new Map<string, Set<() => void>>()

export function runKey(scope: string, nodeId: string): string {
  return `${scope} ${nodeId}`
}

export function activeRun(key: string): ActiveRun | undefined {
  return activeRuns.get(key)
}

export function startRun(key: string, run: ActiveRun): void {
  activeRuns.set(key, run)
}

/** Drops the run if it is still the one registered under `key`; true when it was. */
export function finishRun(key: string, run: ActiveRun): boolean {
  if (activeRuns.get(key) !== run) return false
  activeRuns.delete(key)
  return true
}

export function runSnapshot(key: string): RunSnapshot {
  return snapshots.get(key) ?? IDLE_RUN
}

export function publishRun(key: string, patch: Partial<RunSnapshot>): void {
  snapshots.set(key, { ...runSnapshot(key), ...patch })
  listeners.get(key)?.forEach((listener) => listener())
}

export function subscribeRun(key: string, listener: () => void): () => void {
  const set = listeners.get(key) ?? new Set()
  set.add(listener)
  listeners.set(key, set)
  return () => {
    set.delete(listener)
    if (!set.size) listeners.delete(key)
  }
}

/** Aborts the run under `key` and frees the node straight away, even if the provider is slow to notice. */
export function cancelRun(key: string): void {
  const run = activeRuns.get(key)
  if (!run) return
  run.controller.abort()
  activeRuns.delete(key)
  publishRun(key, { running: false, progress: null })
}

export function hasScopeRuns(scope: string): boolean {
  for (const run of activeRuns.values()) if (run.scope === scope) return true
  return false
}

/** Cancels the scope's runs, or only those whose node `keep` rejects. */
export function cancelScopeRuns(scope: string, keep?: (nodeId: string) => boolean): void {
  for (const [key, run] of [...activeRuns]) {
    if (run.scope === scope && !keep?.(run.nodeId)) cancelRun(key)
  }
}

/** Forgets a finished editor's snapshots. */
export function forgetScopeRuns(scope: string): void {
  const prefix = runKey(scope, '')
  for (const key of [...snapshots.keys()]) if (key.startsWith(prefix)) snapshots.delete(key)
}
